import { useContext, useEffect, useRef, useState } from 'react';
import { Product, SimilarProductsProps } from '../interface/interface';
import { ArrowLeft, ArrowRight } from '../assets/svgImages';
import ProductCards from './ProductCards';
import showToast from './ToastComponent';
import { useAuth } from 'react-oidc-context';
import { CartContext } from '../context/cartContext';
import instance from '../config/axios';
import { calculateOutOfStockEnum } from '../utils/outOfStockStatus';
import Loading from './Loading';
import { RespMessages } from '../constant/constant';

const SimilarProducts = ({
  products,
  isGuest = false,
  substituteProduct,
}: SimilarProductsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { cart, setCart } = useContext(CartContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollButtons();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [products]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -240 : 240,
      behavior: 'smooth',
    });
  };

  const callCart = async () => {
    try {
      setLoading(true);
      const token = user?.access_token;

      const response = await instance('/api/v1/cart', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const responseData = response?.data;
      setCart(responseData);
    } catch (error: any) {
      showToast({
        message: 'Unable to fetch existing cart!',
        type: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  async function addProductToCart(id: string, substituteForId?: string) {
    try {
      setLoading(true);
      const data = {
        product_id: id,
        substituteForId,
      };

      const res = await instance.post('/api/v1/cart/add', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 500;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item added to your cart',
          type: 'success',
        });

        await callCart();
      } else {
        let message = res.data.message;

        if (message === RespMessages.EMAILINVALID)
          message = `You cannot place orders as your email is invalid.
        Please update your email address in your profile. Note, this will also change your login username.`;

        showToast({
          message: message,
          type: 'warning',
          timeout: 6000,
        });
      }
    } catch (error: any) {
      //Do Nothing
      console.error(error);
      showToast({
        message: 'Failed to add item to the cart',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  async function removeProductFromCart(id: string) {
    try {
      setLoading(true);
      const data = {
        product_id: id,
      };

      const res = await instance.post('/api/v1/cart/remove', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 505;
        },
      });

      if (res?.status === 200) {
        showToast({
          message: 'Item removed from your cart',
          type: 'success',
        });
        await callCart();
      } else {
        showToast({
          message: res?.data?.message,
          type: 'warning',
        });
      }
    } catch (error: any) {
      console.error(error);
      showToast({
        message: 'Failed to remove item from the cart',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  function frameProductObject(product: Product) {
    const orderedQuantity =
      cart?.line_items?.find(
        (item: any) => `${item?.product_id}` === `${product.product_code}`,
      )?.quantity ?? 0;

    if (substituteProduct) {
      const subProduct = substituteProduct;
      const subOrderedQuantity =
        cart?.line_items?.find(
          (item: any) => `${item?.product_id}` === `${subProduct.product_code}`,
        )?.quantity ?? 0;

      return {
        ...product,
        substituteProduct: {
          ...subProduct,
          productMaxQty: product?.productMaxQty ?? product.inventory_quantity,
          orderedQuantity: subOrderedQuantity,
          outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
        },
        productMaxQty: product?.productMaxQty ?? product.inventory_quantity,
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    }

    return {
      ...product,
      orderedQuantity,
      outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
    };
  }

  if (!products?.length) return null;

  if (loading) return <Loading />;

  return (
    <div>
      <h2 className="text-lg md:text-xl xl:text-2xl font-semibold text-primary-blue-500 mb-4 lg:mb-5">
        You may also like
      </h2>
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide"
        >
          {products?.map((product) => (
            <div
              key={product.id}
              className="shrink-0 snap-start basis-1/2 sm-land:basis-1/3 lg:basis-1/4"
            >
              <ProductCards
                product={frameProductObject(product)}
                addProductToCart={addProductToCart}
                removeProductFromCart={removeProductFromCart}
                isAccessories={false}
                allowedProduct={false}
                isGuest={isGuest}
                substitueCard={false}
              />
            </div>
          ))}
        </div>

        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scroll('left')}
            className={`hidden sm:flex absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200
          rounded-full w-8 h-8 items-center justify-center shadow hover:bg-gray-50
          transition-colors z-10 ${products.length > 4 ? '' : 'md:hidden'}`}
            aria-label="Scroll left"
          >
            <ArrowLeft />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scroll('right')}
            className={`hidden sm:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200
          rounded-full w-8 h-8 items-center justify-center shadow hover:bg-gray-50
          transition-colors z-10 ${products.length > 4 ? '' : 'md:hidden'}`}
            aria-label="Scroll right"
          >
            <ArrowRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default SimilarProducts;
